import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ConversationAttachment, ConversationUploadContext } from '@/types/attachment';

export interface UseConversationAttachmentsResult {
  attachments: ConversationAttachment[];
  isLoading: boolean;
  isUploading: boolean;
  isResyncing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Upload one file; on success refetch the list and return the created
   *  rows (parent + any unpacked zip members) so the caller can reference
   *  the new attachment id when sending a message. `targetThreadId` covers a
   *  thread created a moment ago that the hook's `threadId` hasn't caught
   *  up with. */
  upload: (file: File, targetThreadId?: string) => Promise<ConversationAttachment[] | null>;
  /** Re-pull missed Freshdesk history + attachments on demand and update the
   *  list (for pre-existing threads / files that arrived before the thread
   *  was opened). */
  resync: () => Promise<void>;
}

/**
 * "Files in this conversation" — list + upload for a chat thread. No
 * internal polling; the message poll plus an explicit refetch after
 * upload/send keep the list fresh.
 *
 * `context` is the page the panel is mounted on. The client conversation
 * is one thread per case, so uploads carry the page's entity to tell the
 * backend which request a client's file is about.
 */
export function useConversationAttachments(
  threadId: string | undefined,
  context?: ConversationUploadContext
): UseConversationAttachmentsResult {
  const [attachments, setAttachments] = useState<ConversationAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!threadId) {
      setAttachments([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listConversationAttachments(threadId);
      setAttachments(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const upload = useCallback(
    async (file: File, targetThreadId?: string): Promise<ConversationAttachment[] | null> => {
      const target = targetThreadId ?? threadId;
      if (!target) {
        return null;
      }
      setIsUploading(true);
      setError(null);
      try {
        const created = await vnextApiClient.uploadConversationAttachment(
          target,
          file,
          undefined,
          context
        );
        await fetchOnce();
        return created;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        setError(e);
        notifications.show({
          title: 'Upload failed',
          message: e.message,
          color: 'red',
        });
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [threadId, context, fetchOnce]
  );

  const resync = useCallback(async (): Promise<void> => {
    if (!threadId) {
      return;
    }
    setIsResyncing(true);
    setError(null);
    try {
      const result = await vnextApiClient.resyncConversationAttachments(threadId);
      setAttachments(result);
      notifications.show({
        title: 'Files re-synced',
        message: `${result.length} file(s) in this conversation.`,
        color: 'green',
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
      notifications.show({ title: 'Re-sync failed', message: e.message, color: 'red' });
    } finally {
      setIsResyncing(false);
    }
  }, [threadId]);

  return {
    attachments,
    isLoading,
    isUploading,
    isResyncing,
    error,
    refetch: fetchOnce,
    upload,
    resync,
  };
}
